import { useMemo } from 'react';
import { getStayRoomsFilters } from '@koob/utils';
import useTripStore from '../../store/useTripStore';
import { getTripDayHotelFlags } from '../../logic/getTripDayHotelFlags';
import {
  useDay,
  useHotel,
  useExperience,
  useHotelGroupForDay,
  useDays,
  useTripFilters,
} from '../../hooks/useTripStoreSelectors';
import { useHotelRoomAvailability } from '../../hooks/useHotelRoomAvailability';
import { useRoomSelectionRefresh } from '../../hooks/useRoomSelectionRefresh';
import { useTripHotelPricing } from '../../hooks/useTripHotelPricing';
import {
  useHotelStayDateRange,
  useHotelGroupStatus,
  useSyncHotelGroupLoadingState,
  useDisplayedRooms,
  isHotelAvailabilityQueryEnabled,
} from './useTripDayHotelStaySync';
import { useClearStaleRoomSelection } from './useTripDayHotelStayDateSync';

// Fetches this day's hotel-stay data (the room group it belongs to, the
// rooms-availability query, and the derived loading/error state every day
// in the group shares) and clears a stale room selection when the stay's
// nights or room count changes. `useTripDayHotelSelection` (the sibling
// hook) picks up from here to decide WHICH rooms end up selected.
export function useTripDayHotelData(dayIndex: number) {
  const tripFilters = useTripFilters();

  const days = useDays();
  const day = useDay(dayIndex);
  const hotel = useHotel(dayIndex);
  const experience = useExperience(dayIndex);
  const updateDay = useTripStore((state) => state.updateDay);

  const dayRoomSelection = day?.selectedRoom;
  const program = experience?.programs?.[(day?.experienceDayIndex ?? 1) - 1];

  const departureDate = tripFilters?.startAt || tripFilters?.departureDate;
  const hotelGroup = useHotelGroupForDay(dayIndex, departureDate);

  const {
    hotelIsNotDistributed,
    suggestedHotelIds,
    hotelImage,
    isIncludedHotel,
    nights,
    isFirstInGroup,
    isMinStayUnmet,
  } = getTripDayHotelFlags({ dayIndex, day, hotel, hotelGroup, program });

  const stayRooms = hotelGroup?.dayIndices
    ?.map((idx: number) => days[idx]?.roomsComposition)
    .find((rooms: any) => rooms?.length);
  const roomsComposition = useMemo(
    () => getStayRoomsFilters(stayRooms, tripFilters?.composition),
    [stayRooms, tripFilters?.composition],
  );

  const { bookedRooms, displayedRooms } = useDisplayedRooms({
    hotel,
    stayRooms,
    roomsComposition,
  });

  const totalTravelers = tripFilters?.composition?.count ?? 0;

  const { roomStartDate, roomEndDate } = useHotelStayDateRange({
    hotelGroup,
    departureDate,
  });

  const isRoomSearchEnabled = isHotelAvailabilityQueryEnabled({
    isFirstInGroup,
    isIncludedHotel,
    hotelIsNotDistributed,
    hotel,
  });

  const {
    data: roomsData,
    isLoading: roomsLoading,
    isFetching: roomsFetching,
    error: roomsError,
  } = useHotelRoomAvailability({
    hotelId: hotel?.id,
    startDate: roomStartDate,
    endDate: roomEndDate,
    roomsComposition,
    enabled: isRoomSearchEnabled,
  } as any);

  // KD-3594 D4 — reconciles this day's room selection whenever the query
  // above gets a fresh requestId (refetched silently on expiry). A no-op for
  // every day but the group's first, since only that day ever carries
  // `selectedRoom`.
  useRoomSelectionRefresh(dayIndex, roomsData);

  // US-058 — prices this day's hotel stay from its selected rooms, outside
  // the component. A no-op for every day but the group's first, same reason
  // as useRoomSelectionRefresh above.
  useTripHotelPricing(dayIndex, roomsData);

  useSyncHotelGroupLoadingState({
    dayIndex,
    hotel,
    isFirstInGroup,
    hotelGroup,
    days,
    day,
    roomsData,
    roomsLoading,
    roomsFetching,
    roomsError,
    updateDay,
  });

  const { isGroupLoading, hasGroupError } = useHotelGroupStatus({
    hotelGroup,
    isIncludedHotel,
    isFirstInGroup,
    days,
    roomsError,
    roomsData,
  });

  useClearStaleRoomSelection({
    dayIndex,
    hotel,
    isFirstInGroup,
    nights,
    roomsComposition,
    roomStartDate,
    isRoomSearchEnabled,
    days,
    dayRoomSelection,
    updateDay,
  });

  return {
    tripFilters,
    days,
    day,
    hotel,
    hotelIsNotDistributed,
    dayRoomSelection,
    program,
    suggestedHotelIds,
    hotelImage,
    hotelGroup,
    roomsComposition,
    bookedRooms,
    displayedRooms,
    totalTravelers,
    isIncludedHotel,
    nights,
    isFirstInGroup,
    isMinStayUnmet,
    roomStartDate,
    roomEndDate,
    roomsData,
    roomsLoading,
    roomsFetching,
    roomsError,
    isGroupLoading,
    hasGroupError,
  };
}
